import React, { useEffect, useRef, useState } from 'react';
import { RocheUIHeroTeaser, RocheUIImageRatio } from '@repo/roche-ui';
import { TrackingService } from '@rocheglobal/component-library-dot-com/src/services/tracking';
import { LinkAnalyzer } from '../../services/link-analyzer';
import { blokToComponent } from '../../helpers/blok-to-component';
import { parseAssetSource } from '../../utils/asset';
import { BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { RocheHeroTeaserImage, RocheHeroTeaserType } from './roche-hero-teaser';
import { StringService } from '../../services/string';
import { useTranslation } from '../../hooks/use-translation';

type RocheHeroTeaserExternalType = {
  teaser_overline: string;
  teaser_title: string;
  teaser_description: string;
  component: string;
  page: string;
  teaser_image: RocheHeroTeaserImage;
};

type RocheHeroTeaserExternalProps = BlokToComponentProps<
  RocheHeroTeaserExternalType & Omit<RocheHeroTeaserType, 'page' | 'slotted_hero_image'>
>;

export const RocheHeroTeaserExternal = ({ blok, slot, getComponent }: RocheHeroTeaserExternalProps): JSX.Element => {
  const [internalLink, setInternalLink] = useState(blok.page);

  const secondaryButtonRef = useRef<HTMLDivElement>(null);
  const showOverline = blok.layout === 'teaser' ? blok.teaser_overline : undefined;
  const readMoreTranslation = useTranslation('read more');
  const primaryButtonText =
    blok.button_title !== '' && blok.button_title ? blok.button_title : StringService.capitalize(readMoreTranslation);

  const secondaryButton = blokToComponent({
    getComponent,
    blok: blok.slotted_secondary_action[0],
  });

  const trackPrimaryButtonClick = () => {
    TrackingService.push({
      event: 'teaser_click',
      component_name: 'roche-hero-teaser',
      additionalParameters: {
        teaser_title: blok.teaser_title,
        teaser_url: internalLink,
      },
    });
  };

  useEffect(() => {
    const { internalLinkUrl } = LinkAnalyzer.analyzeLink(blok.page);
    setInternalLink(internalLinkUrl);
  }, [blok.page]);

  // remove this when button component is moved to roche ui lib
  useEffect(() => {
    const secondaryButton = secondaryButtonRef.current?.querySelector('roche-button') as unknown as HTMLElement;
    if (secondaryButton) {
      secondaryButton.style.margin = '0px';
      secondaryButton.style.padding = '0px';
    }
  }, [secondaryButtonRef.current]);

  return (
    <div slot={slot}>
      <RocheUIHeroTeaser
        teaserTitle={blok.teaser_title}
        teaserOverline={showOverline}
        teaserDescription={blok.teaser_description}
        teaserImage={parseAssetSource(blok.teaser_image[0]?.src.filename) ?? blok.teaser_image[0]?.brightcove_id}
        teaserImageRatio={blok.teaser_image[0]?.ratio as RocheUIImageRatio}
        primaryButtonText={primaryButtonText}
        primaryButtonUrl={internalLink}
        onPrimaryButtonClick={trackPrimaryButtonClick}
        secondaryButton={secondaryButton && <div ref={secondaryButtonRef}>{secondaryButton}</div>}
        leftIcon={blok.left_icon}
        rightIcon={blok.right_icon}
        imageOnLeftSide={blok.mirror_layout}
      />
    </div>
  );
};
